import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth, API, getActiveAccountHeader } from '../App';
import { useSubscription } from '../contexts/SubscriptionContext';
import { Layout } from '../components/Layout';
import { TeamManagement } from '../components/TeamManagement';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { User, Mail, Calendar, Crown, LogOut, Building2, Phone, Lock, Check, Star, Loader2, CreditCard, ExternalLink, RefreshCw, Gift, Trash2, Plus, BarChart3 } from 'lucide-react';
import { format, parseISO, differenceInDays } from 'date-fns';

export const AccountPage = () => {
  const { user, logout, token, refreshUser } = useAuth();
  const { isPro, isRider, isTeamMember } = useSubscription();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState(user?.name || '');
  const [phoneNumber, setPhoneNumber] = useState(user?.phone_number || '');
  const [businessName, setBusinessName] = useState(user?.business_name || '');
  const [useBusinessAsOwner, setUseBusinessAsOwner] = useState(user?.use_business_as_owner || false);
  const [isSaving, setIsSaving] = useState(false);
  
  // Password change state
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  
  // Subscription state
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [isCheckingPayment, setIsCheckingPayment] = useState(false);
  const [isOpeningPortal, setIsOpeningPortal] = useState(false);
  const [isChangingPlan, setIsChangingPlan] = useState(false);
  const [billingInterval, setBillingInterval] = useState('monthly'); // 'monthly' or 'yearly'
  
  // Team state - check if user is a team member (not owner)
  const [teamInfo, setTeamInfo] = useState(null);

  // Promo accounts state (admin only)
  const [promoAccounts, setPromoAccounts] = useState([]);
  const [promoDialogOpen, setPromoDialogOpen] = useState(false);
  const [newPromoEmail, setNewPromoEmail] = useState('');
  const [newPromoName, setNewPromoName] = useState('');
  const [newPromoNotes, setNewPromoNotes] = useState('');
  const [isAddingPromo, setIsAddingPromo] = useState(false);
  
  // Admin stats state (owner only)
  const [adminStats, setAdminStats] = useState(null);
  
  // Owner email check
  const isAppOwner = user?.email?.toLowerCase() === 'countryheartscollective@gmail.com';

  // Get headers dynamically to ensure fresh activeAccountId after account switch
  const getHeaders = () => {
    const activeAccountId = localStorage.getItem('activeAccountId') || sessionStorage.getItem('activeAccountId');
    const h = { 'Content-Type': 'application/json' };
    if (activeAccountId) h['X-Active-Account'] = activeAccountId;
    if (token) h['Authorization'] = `Bearer ${token}`;
    return h;
  };

  // Check for payment return
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const status = searchParams.get('status');
    
    if (sessionId && status === 'success') {
      checkPaymentStatus(sessionId);
    } else if (status === 'cancelled') {
      toast.info('Payment cancelled');
      setSearchParams({});
    }
  }, [searchParams]);

  // Fetch subscription status
  useEffect(() => {
    if (token) {
      fetchSubscriptionStatus();
      fetchTeamInfo();
      fetchPromoAccounts();
      if (isAppOwner) {
        fetchAdminStats();
      }
    }
  }, [token, isAppOwner]);

  const fetchSubscriptionStatus = async () => {
    try {
      const response = await fetch(`${API}/payments/subscription`, {
        headers: getHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setSubscriptionStatus(data);
      }
    } catch (error) {
      console.error('Failed to fetch subscription:', error);
    }
  };
  
  const fetchTeamInfo = async () => {
    try {
      const response = await fetch(`${API}/team`, {
        headers: getHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setTeamInfo(data);
      }
    } catch (error) {
      console.error('Failed to fetch team info:', error);
    }
  };

  // Fetch promo accounts (admin only)
  const fetchPromoAccounts = async () => {
    if (!isAppOwner) return;
    try {
      const response = await fetch(`${API}/promo-accounts`, {
        headers: getHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setPromoAccounts(data);
      }
    } catch (error) {
      console.error('Failed to fetch promo accounts:', error);
    }
  };

  // Fetch admin stats (owner only)
  const fetchAdminStats = async () => {
    try {
      const response = await fetch(`${API}/promo-accounts/admin-stats`, {
        headers: getHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setAdminStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch admin stats:', error);
    }
  };

  // Add promo account
  const handleAddPromoAccount = async (e) => {
    e.preventDefault();
    if (!newPromoEmail.trim() || !newPromoName.trim()) {
      toast.error('Email and name are required');
      return;
    }
    
    setIsAddingPromo(true);
    try {
      const response = await fetch(`${API}/promo-accounts`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          email: newPromoEmail.trim(),
          name: newPromoName.trim(),
          notes: newPromoNotes.trim() || null
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setPromoAccounts([data, ...promoAccounts]);
        setNewPromoEmail('');
        setNewPromoName('');
        setNewPromoNotes('');
        setPromoDialogOpen(false);
        toast.success(`Promo access granted to ${data.name}`);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to add promo account');
      }
    } catch (error) {
      toast.error('Failed to add promo account');
    } finally {
      setIsAddingPromo(false);
    }
  };

  // Remove promo account
  const handleRemovePromoAccount = async (email, name) => {
    if (!confirm(`Remove promo access for ${name}?`)) return;
    
    try {
      const response = await fetch(`${API}/promo-accounts/${encodeURIComponent(email)}`, {
        method: 'DELETE',
        headers: getHeaders()
      });
      
      if (response.ok) {
        setPromoAccounts(promoAccounts.filter(p => p.email !== email));
        toast.success(`Promo access removed for ${name}`);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to remove promo account');
      }
    } catch (error) {
      toast.error('Failed to remove promo account');
    }
  };
  
  const handleOpenPortal = async () => {
    setIsOpeningPortal(true);
    try {
      const response = await fetch(`${API}/payments/portal`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          return_url: window.location.href.split('?')[0]
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        window.location.href = data.portal_url;
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to open billing portal');
      }
    } catch (error) {
      toast.error('Failed to open billing portal');
    } finally {
      setIsOpeningPortal(false);
    }
  };

  const checkPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    
    if (attempts >= maxAttempts) {
      toast.error('Payment status check timed out. Please refresh the page.');
      setSearchParams({});
      return;
    }
    
    setIsCheckingPayment(true);
    
    try {
      const response = await fetch(`${API}/payments/status/${sessionId}`, {
        headers: getHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        
        if (data.payment_status === 'paid') {
          toast.success('Subscription activated! Welcome to EquiVault ' + (data.package_id === 'pro' ? 'Pro' : 'Rider') + '!');
          setSearchParams({});
          fetchSubscriptionStatus();
          refreshUser();
          setIsCheckingPayment(false);
          return;
        } else if (data.status === 'expired') {
          toast.error('Payment session expired. Please try again.');
          setSearchParams({});
          setIsCheckingPayment(false);
          return;
        }
        
        // Still processing, poll again
        setTimeout(() => checkPaymentStatus(sessionId, attempts + 1), 2000);
      }
    } catch (error) {
      console.error('Payment check error:', error);
      toast.error('Error checking payment status');
      setIsCheckingPayment(false);
    }
  };

  const handleSubscribe = async (packageId) => {
    setIsSubscribing(true);
    
    try {
      const originUrl = window.location.origin;
      const response = await fetch(`${API}/payments/subscribe`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          package_id: packageId,
          billing_interval: billingInterval,
          origin_url: originUrl
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        // Redirect to Stripe checkout
        window.location.href = data.checkout_url;
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to start checkout');
      }
    } catch (error) {
      toast.error('Failed to start checkout');
    } finally {
      setIsSubscribing(false);
    }
  };

  const handleChangePlan = async (newPlan) => {
    const currentPlan = user?.subscription_plan;
    const isUpgrade = newPlan === 'pro';
    const action = isUpgrade ? 'upgrade to Pro' : 'downgrade to Rider';
    
    if (!confirm(`Are you sure you want to ${action}?\n\n${isUpgrade 
      ? 'You\'ll be charged the difference immediately and get access to unlimited horses and team members.' 
      : 'You\'ll receive a prorated credit. Your horse limit will become 5 and team limit will become 1.'}`)) {
      return;
    }
    
    setIsChangingPlan(true);
    
    try {
      const response = await fetch(`${API}/payments/change-plan`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ plan: newPlan })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message || `Successfully changed to ${newPlan} plan!`);
        // Refresh user data to update UI
        if (refreshUser) refreshUser();
        // Reload after a short delay to ensure all states update
        setTimeout(() => window.location.reload(), 1500);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to change plan');
      }
    } catch (error) {
      toast.error('Failed to change plan');
    } finally {
      setIsChangingPlan(false);
    }
  };

  // Update local state when user changes
  useEffect(() => {
    if (user) {
      setName(user.name || '');
      setPhoneNumber(user.phone_number || '');
      setBusinessName(user.business_name || '');
      setUseBusinessAsOwner(user.use_business_as_owner || false);
    }
  }, [user]);

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      const response = await fetch(`${API}/account`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify({ 
          name,
          phone_number: phoneNumber || null,
          business_name: businessName || null,
          use_business_as_owner: useBusinessAsOwner
        })
      });

      if (response.ok) {
        const updatedUser = await response.json();
        toast.success('Profile updated!');
        setIsEditing(false);
        // Refresh user data from the response
        if (refreshUser) {
          await refreshUser();
        }
      } else {
        toast.error('Failed to update profile');
      }
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setIsSaving(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    window.location.href = '/';
  };

  const handleClearCacheAndReload = async () => {
    try {
      // Clear all local storage
      localStorage.clear();
      sessionStorage.clear();
      
      // Unregister service workers
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        for (const registration of registrations) {
          await registration.unregister();
        }
      }
      
      // Clear caches
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        for (const name of cacheNames) {
          await caches.delete(name);
        }
      }
      
      toast.success('Cache cleared! Reloading...');
      
      // Force a hard reload bypassing all caches
      setTimeout(() => {
        // Add cache-busting parameter to force fresh load
        window.location.href = '/?clearcache=' + Date.now();
      }, 1000);
    } catch (error) {
      console.error('Error clearing cache:', error);
      // Still reload even if there's an error
      window.location.href = '/?clearcache=' + Date.now();
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setIsChangingPassword(true);
    
    try {
      const response = await fetch(`${API}/auth/change-password`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword
        })
      });
      
      if (response.ok) {
        toast.success('Password changed successfully');
        setPasswordDialogOpen(false);
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to change password');
      }
    } catch (error) {
      toast.error('Failed to change password');
    } finally {
      setIsChangingPassword(false);
    }
  };

  const getPlanBadge = (plan) => {
    const plans = {
      trial: { label: '14-Day Trial', className: 'bg-amber-500 text-white' },
      free: { label: 'Trial Expired', className: 'bg-muted text-muted-foreground' },
      rider: { label: 'EquiVault Rider', className: 'bg-saddle text-white' },
      pro: { label: 'EquiVault Pro', className: 'bg-forest text-white' }
    };
    return plans[plan] || plans.free;
  };

  // Calculate trial days remaining
  const getTrialDaysRemaining = () => {
    if (user?.trial_end) {
      const days = differenceInDays(parseISO(user.trial_end), new Date());
      return Math.max(0, days);
    }
    return 0;
  };

  const trialDaysRemaining = getTrialDaysRemaining();
  const isTrialActive = user?.subscription_plan === 'trial' && trialDaysRemaining > 0;

  const planBadge = getPlanBadge(user?.subscription_plan);

  return (
    <Layout>
      <div className="p-4 md:p-6 lg:p-8 max-w-2xl" data-testid="account-page">
        <h1 className="text-2xl md:text-3xl font-bold font-serif text-foreground mb-6">Account</h1>

        {/* Profile Card */}
        <Card className="border-0 shadow-card bg-white mb-6">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <User className="w-5 h-5 text-saddle" />
              Profile
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {user?.picture && (
              <div className="flex justify-center mb-4">
                <img 
                  src={user.picture} 
                  alt={user.name} 
                  className="w-20 h-20 rounded-full object-cover border-4 border-muted"
                />
              </div>
            )}

            {isEditing ? (
              <form onSubmit={handleUpdateProfile} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Name</Label>
                  <Input 
                    id="name" 
                    value={name} 
                    onChange={(e) => setName(e.target.value)}
                    data-testid="name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone_number" className="flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    Phone Number
                  </Label>
                  <Input 
                    id="phone_number" 
                    value={phoneNumber} 
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    placeholder="e.g., +1 555-123-4567"
                    data-testid="phone-number-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="business_name" className="flex items-center gap-2">
                    <Building2 className="w-4 h-4" />
                    Business/Stud Name
                  </Label>
                  <Input 
                    id="business_name" 
                    value={businessName} 
                    onChange={(e) => setBusinessName(e.target.value)}
                    placeholder="e.g., Sunrise Stables, Valley View Stud"
                    data-testid="business-name-input"
                  />
                </div>

                {businessName && (
                  <div className="flex items-center space-x-2 p-3 bg-muted/50 rounded-lg">
                    <Checkbox 
                      id="use_business_as_owner" 
                      checked={useBusinessAsOwner}
                      onCheckedChange={(checked) => setUseBusinessAsOwner(checked)}
                      data-testid="use-business-as-owner-checkbox"
                    />
                    <label 
                      htmlFor="use_business_as_owner" 
                      className="text-sm cursor-pointer leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      Use business name as default horse owner
                    </label>
                  </div>
                )}

                <div className="flex gap-2">
                  <Button type="submit" disabled={isSaving} className="bg-forest hover:bg-forest-dark" data-testid="save-profile-btn">
                    {isSaving ? 'Saving...' : 'Save'}
                  </Button>
                  <Button type="button" variant="outline" onClick={() => {
                    setIsEditing(false);
                    setName(user?.name || '');
                    setPhoneNumber(user?.phone_number || '');
                    setBusinessName(user?.business_name || '');
                    setUseBusinessAsOwner(user?.use_business_as_owner || false);
                  }}>
                    Cancel
                  </Button>
                </div>
              </form>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                  <User className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Name</p>
                    <p className="font-medium">{user?.name}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                  <Mail className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Email</p>
                    <p className="font-medium">{user?.email}</p>
                  </div>
                </div>
                {user?.phone_number && (
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                    <Phone className="w-5 h-5 text-muted-foreground" />
                    <div>
                      <p className="text-sm text-muted-foreground">Phone Number</p>
                      <p className="font-medium">{user.phone_number}</p>
                    </div>
                  </div>
                )}
                {user?.business_name && (
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                    <Building2 className="w-5 h-5 text-muted-foreground" />
                    <div>
                      <p className="text-sm text-muted-foreground">Business/Stud Name</p>
                      <p className="font-medium">{user.business_name}</p>
                      {user.use_business_as_owner && (
                        <p className="text-xs text-forest mt-1">Used as default horse owner</p>
                      )}
                    </div>
                  </div>
                )}
                {user?.created_at && (
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                    <Calendar className="w-5 h-5 text-muted-foreground" />
                    <div>
                      <p className="text-sm text-muted-foreground">Member Since</p>
                      <p className="font-medium">
                        {format(parseISO(user.created_at), 'MMMM d, yyyy')}
                      </p>
                    </div>
                  </div>
                )}
                <Button variant="outline" onClick={() => setIsEditing(true)} data-testid="edit-profile-btn">
                  Edit Profile
                </Button>
                
                {/* Change Password - only for non-social login */}
                {user?.has_password && (
                  <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
                    <DialogTrigger asChild>
                      <Button variant="outline" className="gap-2" data-testid="change-password-btn">
                        <Lock className="w-4 h-4" />
                        Change Password
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Change Password</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleChangePassword} className="space-y-4 mt-4">
                        <div className="space-y-2">
                          <Label>Current Password</Label>
                          <Input 
                            type="password" 
                            value={currentPassword}
                            onChange={(e) => setCurrentPassword(e.target.value)}
                            required
                            data-testid="current-password-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>New Password</Label>
                          <Input 
                            type="password" 
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            minLength={6}
                            required
                            data-testid="new-password-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Confirm New Password</Label>
                          <Input 
                            type="password" 
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            minLength={6}
                            required
                            data-testid="confirm-password-input"
                          />
                        </div>
                        <Button 
                          type="submit" 
                          className="w-full bg-forest hover:bg-forest-dark"
                          disabled={isChangingPassword}
                          data-testid="submit-password-btn"
                        >
                          {isChangingPassword ? 'Changing...' : 'Change Password'}
                        </Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Subscription Card */}
        <Card className="border-0 shadow-card bg-white mb-6">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Crown className="w-5 h-5 text-saddle" />
              Subscription
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="p-4 rounded-lg bg-muted/50 mb-6">
              <div className="flex items-center justify-between mb-2">
                <Badge className={planBadge.className}>{planBadge.label}</Badge>
                {isTrialActive && (
                  <span className="text-sm text-amber-600 font-medium">
                    {trialDaysRemaining} days remaining
                  </span>
                )}
              </div>
              <p className="text-sm text-muted-foreground">
                {isTrialActive 
                  ? 'Full access to all features during your trial period'
                  : user?.subscription_plan === 'rider'
                  ? 'Up to 5 horses, invite 1 helper to your account'
                  : user?.subscription_plan === 'pro'
                  ? 'Unlimited horses, invite your whole team'
                  : 'Your trial has expired. Choose a plan to continue.'}
              </p>
              
              {/* Manage Subscription Button - only for paying subscribers */}
              {(user?.subscription_plan === 'rider' || user?.subscription_plan === 'pro') && (
                <Button 
                  variant="outline" 
                  className="mt-4 gap-2"
                  onClick={handleOpenPortal}
                  disabled={isOpeningPortal}
                  data-testid="manage-subscription-btn"
                >
                  {isOpeningPortal ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Opening...</>
                  ) : (
                    <><CreditCard className="w-4 h-4" /> Manage Subscription</>
                  )}
                </Button>
              )}
            </div>

            {/* Plan Comparison */}
            <div className="space-y-4">
              {/* Currency Notice - More Prominent */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-3">
                <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-amber-700 font-bold text-sm">$</span>
                </div>
                <div>
                  <p className="text-sm font-medium text-amber-800">Prices shown in NZD (New Zealand Dollars)</p>
                  <p className="text-xs text-amber-700 mt-0.5">Stripe will automatically convert to your local currency at checkout</p>
                </div>
              </div>

              <div className="flex items-center justify-between flex-wrap gap-2">
                <h4 className="font-semibold">Choose Your Plan</h4>
                {/* Billing Interval Toggle */}
                <div className="flex items-center gap-2 p-1 bg-muted rounded-lg">
                  <button
                    type="button"
                    className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                      billingInterval === 'monthly' 
                        ? 'bg-white shadow-sm text-foreground' 
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                    onClick={() => setBillingInterval('monthly')}
                    data-testid="billing-monthly-btn"
                  >
                    Monthly
                  </button>
                  <button
                    type="button"
                    className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                      billingInterval === 'yearly' 
                        ? 'bg-white shadow-sm text-foreground' 
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                    onClick={() => setBillingInterval('yearly')}
                    data-testid="billing-yearly-btn"
                  >
                    Yearly
                    <span className="ml-1 text-xs text-forest font-semibold">Save 20%</span>
                  </button>
                </div>
              </div>
              
              <div className="grid gap-4">
                {/* Rider Plan */}
                <div className={`p-4 rounded-lg border-2 ${user?.subscription_plan === 'rider' ? 'border-saddle bg-saddle/5' : 'border-muted'}`}>
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <span className="font-bold text-lg">EquiVault Rider</span>
                      {user?.subscription_plan === 'rider' && (
                        <Badge className="ml-2 bg-saddle text-white">Current</Badge>
                      )}
                    </div>
                    <div className="text-right">
                      {billingInterval === 'monthly' ? (
                        <>
                          <span className="text-2xl font-bold text-saddle">$8.99</span>
                          <span className="text-muted-foreground">/mo</span>
                        </>
                      ) : (
                        <>
                          <span className="text-2xl font-bold text-saddle">$86</span>
                          <span className="text-muted-foreground">/yr</span>
                          <p className="text-xs text-muted-foreground">~$7.17/mo</p>
                        </>
                      )}
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground mb-3">Perfect for private horse owners</p>
                  <ul className="text-sm space-y-2">
                    <li className="flex items-center gap-2"><Check className="w-4 h-4 text-forest" /> Up to 5 horses</li>
                    <li className="flex items-center gap-2"><Check className="w-4 h-4 text-forest" /> All logs, health records & reminders</li>
                    <li className="flex items-center gap-2"><Check className="w-4 h-4 text-forest" /> Tack Vault & Finance Vault</li>
                    <li className="flex items-center gap-2"><Check className="w-4 h-4 text-forest" /> Document storage</li>
                    <li className="flex items-center gap-2"><Check className="w-4 h-4 text-forest" /> Reports & Contract templates</li>
                    <li className="flex items-center gap-2"><Check className="w-4 h-4 text-forest" /> QR Code ID & Transfer</li>
                    <li className="flex items-center gap-2"><Check className="w-4 h-4 text-forest" /> Invite 1 helper to your account</li>
                  </ul>
                  {user?.subscription_plan !== 'rider' && user?.subscription_plan !== 'pro' && (
                    <Button 
                      className="w-full mt-4 bg-saddle hover:bg-saddle-dark" 
                      data-testid="choose-rider-btn"
                      onClick={() => handleSubscribe('rider')}
                      disabled={isSubscribing || isCheckingPayment}
                    >
                      {isSubscribing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</> : `Choose Rider - ${billingInterval === 'yearly' ? '$86/yr' : '$8.99/mo'}`}
                    </Button>
                  )}
                  {user?.subscription_plan === 'pro' && (
                    <Button 
                      variant="outline"
                      className="w-full mt-4" 
                      data-testid="downgrade-rider-btn"
                      onClick={() => handleChangePlan('rider')}
                      disabled={isChangingPlan}
                    >
                      {isChangingPlan ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</> : 'Switch to Rider'}
                    </Button>
                  )}
                </div>

                {/* Pro Plan */}
                <div className={`p-4 rounded-lg border-2 relative ${user?.subscription_plan === 'pro' ? 'border-forest bg-forest/5' : 'border-forest'}`}>
                  <div className="absolute -top-3 left-4">
                    <Badge className="bg-forest text-white gap-1">
                      <Star className="w-3 h-3" /> Most Popular
                    </Badge>
                  </div>
                  <div className="flex justify-between items-start mb-3 mt-2">
                    <div>
                      <span className="font-bold text-lg">EquiVault Pro</span>
                      {user?.subscription_plan === 'pro' && (
                        <Badge className="ml-2 bg-forest text-white">Current</Badge>
                      )}
                    </div>
                    <div className="text-right">
                      {billingInterval === 'monthly' ? (
                        <>
                          <span className="text-2xl font-bold text-forest">$14.99</span>
                          <span className="text-muted-foreground">/mo</span>
                        </>
                      ) : (
                        <>
                          <span className="text-2xl font-bold text-forest">$144</span>
                          <span className="text-muted-foreground">/yr</span>
                          <p className="text-xs text-muted-foreground">~$12/mo</p>
                        </>
                      )}
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground mb-3">Ideal for trainers, breeders & stables</p>
                  <ul className="text-sm space-y-2">
                    <li className="flex items-center gap-2"><Check className="w-4 h-4 text-forest" /> <strong>Unlimited horses</strong></li>
                    <li className="flex items-center gap-2"><Check className="w-4 h-4 text-forest" /> Everything in Rider plan</li>
                    <li className="flex items-center gap-2"><Check className="w-4 h-4 text-forest" /> Stable dashboard</li>
                    <li className="flex items-center gap-2"><Check className="w-4 h-4 text-forest" /> <strong>Invite unlimited team members</strong></li>
                    <li className="flex items-center gap-2"><Check className="w-4 h-4 text-forest" /> Staff & rider management</li>
                    <li className="flex items-center gap-2"><Check className="w-4 h-4 text-forest" /> Priority support</li>
                  </ul>
                  {user?.subscription_plan !== 'pro' && (
                    <Button 
                      className="w-full mt-4 bg-forest hover:bg-forest-dark" 
                      data-testid="choose-pro-btn"
                      onClick={() => user?.subscription_plan === 'rider' ? handleChangePlan('pro') : handleSubscribe('pro')}
                      disabled={isSubscribing || isCheckingPayment || isChangingPlan}
                    >
                      {(isSubscribing || isChangingPlan) ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</>
                      ) : user?.subscription_plan === 'rider' ? (
                        'Upgrade to Pro'
                      ) : (
                        `Choose Pro - ${billingInterval === 'yearly' ? '$144/yr' : '$14.99/mo'}`
                      )}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Admin Stats - Owner Only */}
        {isAppOwner && adminStats && (
          <Card className="border-0 shadow-card bg-white">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-forest" />
                User Statistics
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 pt-0">
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-4 bg-muted/30 rounded-lg">
                  <p className="text-3xl font-bold text-forest">{adminStats.total_users}</p>
                  <p className="text-sm text-muted-foreground">Total Users</p>
                </div>
                <div className="text-center p-4 bg-muted/30 rounded-lg">
                  <p className="text-3xl font-bold text-amber-600">{adminStats.trial_users}</p>
                  <p className="text-sm text-muted-foreground">On Trial</p>
                </div>
                <div className="text-center p-4 bg-muted/30 rounded-lg">
                  <p className="text-3xl font-bold text-saddle">{adminStats.subscribed_users}</p>
                  <p className="text-sm text-muted-foreground">Subscribed</p>
                </div>
              </div>
              {adminStats.subscribed_users > 0 && (
                <div className="mt-3 pt-3 border-t text-sm text-muted-foreground text-center">
                  {adminStats.rider_users} Rider · {adminStats.pro_users} Pro · {adminStats.promo_accounts} Promo
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Promo Accounts - Admin Only */}
        {isAppOwner && (
          <Card className="border-0 shadow-card bg-white">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Gift className="w-5 h-5 text-saddle" />
                Promo Accounts
              </CardTitle>
              <CardDescription>
                Grant free Pro access to influencers and collaborators
              </CardDescription>
            </CardHeader>
            <CardContent className="p-4 pt-0 space-y-4">
              <Dialog open={promoDialogOpen} onOpenChange={setPromoDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="w-full gap-2 bg-saddle hover:bg-saddle/90">
                    <Plus className="w-4 h-4" />
                    Add Influencer
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add Promo Account</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleAddPromoAccount} className="space-y-4">
                    <div>
                      <Label htmlFor="promo-email">Email Address *</Label>
                      <Input
                        id="promo-email"
                        type="email"
                        value={newPromoEmail}
                        onChange={(e) => setNewPromoEmail(e.target.value)}
                        placeholder="influencer@email.com"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="promo-name">Name *</Label>
                      <Input
                        id="promo-name"
                        value={newPromoName}
                        onChange={(e) => setNewPromoName(e.target.value)}
                        placeholder="Sarah Smith"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="promo-notes">Notes (optional)</Label>
                      <Textarea
                        id="promo-notes"
                        value={newPromoNotes}
                        onChange={(e) => setNewPromoNotes(e.target.value)}
                        placeholder="TikTok - 50K followers, horse training content"
                        rows={2}
                      />
                    </div>
                    <Button 
                      type="submit" 
                      className="w-full bg-saddle hover:bg-saddle/90"
                      disabled={isAddingPromo}
                    >
                      {isAddingPromo ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Adding...</>
                      ) : (
                        'Grant Pro Access'
                      )}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>

              {promoAccounts.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No promo accounts yet. Add influencers to grant them free Pro access.
                </p>
              ) : (
                <div className="space-y-3">
                  {promoAccounts.map((promo) => (
                    <div 
                      key={promo.email} 
                      className="flex items-start justify-between p-3 bg-cream/50 rounded-lg"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{promo.name}</p>
                        <p className="text-xs text-muted-foreground truncate">{promo.email}</p>
                        {promo.notes && (
                          <p className="text-xs text-muted-foreground mt-1">{promo.notes}</p>
                        )}
                        <p className="text-xs text-muted-foreground mt-1">
                          Added {format(parseISO(promo.added_at), 'MMM d, yyyy')}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive hover:bg-destructive/10 ml-2"
                        onClick={() => handleRemovePromoAccount(promo.email, promo.name)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              
              <p className="text-xs text-muted-foreground">
                {promoAccounts.length} / 20 promo accounts used
              </p>
            </CardContent>
          </Card>
        )}

        {/* Troubleshooting */}
        <Card className="border-0 shadow-card bg-white">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-saddle" />
              Troubleshooting
            </CardTitle>
            <CardDescription>
              If data isn't syncing correctly, try clearing the cache
            </CardDescription>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <Button 
              variant="outline" 
              className="w-full gap-2"
              onClick={handleClearCacheAndReload}
              data-testid="clear-cache-btn"
            >
              <RefreshCw className="w-4 h-4" />
              Clear Cache & Reload
            </Button>
          </CardContent>
        </Card>

        {/* Logout */}
        <Card className="border-0 shadow-card bg-white">
          <CardContent className="p-4">
            <Button 
              variant="outline" 
              className="w-full text-destructive hover:text-destructive hover:bg-destructive/10 gap-2"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
              Sign Out
            </Button>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};
